import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  analytics,
  getScreenShownEvent,
  getScreenFilterAppliedEvent,
} from '~entities/amplitude-analytics';
import { useMerchantsListQuery } from '~entities/merchant';

import type { AbstractOption } from '~shared/types/abstract-options';
import type { DateRange } from '~shared/ui/date-picker/date-picker';

import Button from '@platform-ui/button';

import isEmpty from '@tinkoff/utils/is/empty';

import cn from 'classnames';

import { ReactComponent as CrossIcon } from './cross-icon.svg';

import { useSyncedInitialValue } from '../../hooks/use-synced-initial-value';
import { getDefaultFilter } from '../../lib/default-filter';
import { getCorrectPeriod } from '../../lib/get-correct-period';
import { getFilterStorageKey } from '../../lib/get-filter-storage-key';
import { MerchantFilter } from '../merchant-filter/merchant-filter';
import { PeriodFilter } from '../period-filter/period-filter';
import { PosFilter } from '../pos-filter/pos-filter';
import { StatusFilter } from '../status-filter/status-filter';

export type FiltersValue<T = string> = {
  period: DateRange;
  posIds?: string[];
  merchantIds?: string[];
  statuses?: T[];
};

type Props<T extends string> = {
  statusOptions?: AbstractOption<T>[];
  screen: string;
  onFiltersChange: (filters: FiltersValue<T>) => void;
};

export function Filters<T extends string>({
  statusOptions,
  screen,
  onFiltersChange,
}: Props<T>) {
  const methods = useForm<FiltersValue<T>>();
  const {
    data: merchantsList,
    isError: isMerchantsListError,
    isSuccess: isMerchantsListSuccess,
  } = useMerchantsListQuery();

  const handleChangeFilters = () => {
    const filters = methods.getValues();

    if (filters) {
      onFiltersChange(filters);
      window.localStorage.setItem(
        getFilterStorageKey(screen),
        JSON.stringify(filters)
      );
      const period =
        filters.period?.startDate && filters.period.endDate
          ? `${filters.period?.startDate}-${filters.period?.endDate}`
          : '';

      analytics.track(
        getScreenFilterAppliedEvent(screen, {
          filter_period: period,
          filter_chosenMerchantsCount: filters.merchantIds?.length,
          filter_chosenStoresCount: filters.posIds?.length,
          filter_statuses: filters.statuses,
        })
      );
    }
  };

  useSyncedInitialValue({
    statusOptions,
    getInitialValue: () => {
      const localStorageValue = window.localStorage.getItem(
        getFilterStorageKey(screen)
      );
      const parsedValue = localStorageValue && JSON.parse(localStorageValue);

      if (isEmpty(parsedValue)) {
        return getDefaultFilter();
      }

      return {
        ...(parsedValue as FiltersValue<T>),
        period: getCorrectPeriod(parsedValue.period),
      };
    },
    setInitialValue: (initialValue) => {
      methods.reset(initialValue);
      handleChangeFilters();
    },
  });

  const handleResetFilters = () => {
    methods.reset(getDefaultFilter());
    handleChangeFilters();
  };

  useEffect(() => {
    if (isMerchantsListError || isMerchantsListSuccess) {
      analytics.track(getScreenShownEvent(screen, merchantsList?.length));
    }
  }, [screen, isMerchantsListError, isMerchantsListSuccess, merchantsList]);

  const isHolding =
    isMerchantsListSuccess &&
    merchantsList.some((merchant) => merchant.holding);

  const hideMerchantFilter =
    !isMerchantsListSuccess ||
    merchantsList?.length < 2 ||
    (screen === 'payments' && isHolding);
  const hidePosFilter = screen === 'payments';

  return (
    <form>
      <FormProvider {...methods}>
        <div className="flex gap-2">
          <div className="w-fit min-w-[168px]">
            <PeriodFilter onFilterChanged={handleChangeFilters} />
          </div>
          {!hideMerchantFilter && (
            <div
              className={cn({
                'basis-48': statusOptions,
                'min-w-[192px] flex-grow': !statusOptions,
              })}
            >
              <MerchantFilter
                onFilterChanged={() => {
                  methods.setValue('posIds', []);
                  handleChangeFilters();
                }}
              />
            </div>
          )}
          {!hidePosFilter && (
            <div className="min-w-[192px] flex-grow">
              <PosFilter onFilterChanged={handleChangeFilters} />
            </div>
          )}

          {statusOptions && (
            <div
              className={cn({
                'basis-48': !hideMerchantFilter,
                'min-w-[192px] flex-grow': hideMerchantFilter,
              })}
            >
              <StatusFilter
                options={statusOptions}
                onFilterChanged={handleChangeFilters}
              />
            </div>
          )}

          <div>
            <Button theme="secondary" size="s" onClick={handleResetFilters}>
              <CrossIcon />
            </Button>
          </div>
        </div>
      </FormProvider>
    </form>
  );
}
